import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TitleIcon } from '../constants.tsx';
import { fetchMarketFeed, isSaudiMarketOpen, riyadhTime, MarketFeedMeta } from './marketFeed.ts';

interface LivePick {
    name: string;
    code: string;
    change: string;
    changePct: string;
    price: string;
    liquidity: string;
    netLiquidity: string;
    isPositive: boolean;
}

const parseNum = (val: any): number => {
    if (!val || val === '-') return 0;
    const str = String(val).replace(/%/g, '').replace(/,/g, '').trim();
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
};

const Dchip = () => (
    <span
        className="inline-flex items-center justify-center w-4 h-4 rounded bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[10px] font-extrabold"
        title="بيانات الزوار مؤجلة حتى 15 دقيقة"
    >D</span>
);

const LiveAiPicks: React.FC = () => {
    const { t } = useTranslation();
    const [picks, setPicks] = useState<LivePick[]>([]);
    const [meta, setMeta] = useState<MarketFeedMeta | null>(null);
    const [loaded, setLoaded] = useState(false);

    const fetchLivePicks = async () => {
        try {
            const { rows, meta: feedMeta } = await fetchMarketFeed();

            const validStocks = rows.filter(stock => {
                const code = String(stock['الرمز'] || stock['Code'] || '');
                const name = String(stock['الاسم'] || stock['Name'] || '');
                if (!code || !name) return false;
                // السوق الرئيسية فقط: 4 أرقام ولا تبدأ بـ 9
                return /^\d{4}$/.test(code) && !code.startsWith('9');
            });

            const topPicks = [...validStocks]
                .filter(stock => {
                    const changePct = parseNum(stock['التغير %']);
                    const liquidity = parseNum(stock['نسبة السيولة']);
                    return changePct > 0 && liquidity > 50;
                })
                .sort((a, b) => parseNum(b['صافي السيولة']) - parseNum(a['صافي السيولة']))
                .slice(0, 6);

            if (topPicks.length > 0) {
                const formattedPicks: LivePick[] = topPicks.map(stock => {
                    const rawPct = parseNum(stock['التغير %']);
                    const isPos = rawPct >= 0;
                    const pctStr = `${isPos ? '+' : ''}${rawPct.toFixed(2)}%`;
                    const liqVal = parseNum(stock['نسبة السيولة']).toFixed(0);

                    return {
                        name: String(stock['الاسم'] || stock['Name'] || t('livePicksUnknownStock')),
                        code: String(stock['الرمز'] || stock['Code'] || '----'),
                        change: String(stock['التغير'] || '0'),
                        changePct: pctStr,
                        price: String(stock['آخر'] || stock['السعر'] || '0.00'),
                        liquidity: liqVal,
                        netLiquidity: String(stock['صافي السيولة'] || '0'),
                        isPositive: isPos,
                    };
                });

                setPicks(formattedPicks);
            }
            setMeta(feedMeta);
        } catch (error) {
            console.error('Error fetching market picks:', error);
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        fetchLivePicks();
        const interval = setInterval(() => {
            if (isSaudiMarketOpen() && !document.hidden) fetchLivePicks();
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const stamp = meta ? riyadhTime(meta.sourceAt) : '';
    const note = meta
        ? (meta.mode === 'delayed'
            ? t('feedDelayed', { time: stamp })
            : t('feedClose', { time: stamp }))
        : '';

    return (
        <section id="live-picks" className="overflow-hidden pt-16 lg:pt-20 xl:pt-24 scroll-mt-16 relative">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 xl:px-0">
                <div className="text-center">
                    <span className="hero-subtitle-gradient relative mb-4 font-medium text-sm inline-flex items-center gap-2 py-2 px-4.5 rounded-full">
                        <TitleIcon />
                        <span className="hero-subtitle-text">🔥 {t('livePicksSubtitle')}</span>
                    </span>
                    <h2 className="ai-gradient-text mb-4.5 text-2xl font-extrabold sm:text-4xl">
                        {t('livePicksTitle')}
                    </h2>
                    <p className="max-w-3xl mx-auto mb-4 font-medium text-gray-400">
                        {t('livePicksDescription')}
                    </p>
                    {meta && (
                        <div className="flex items-center justify-center gap-2 flex-wrap text-xs text-gray-400 mb-10">
                            <Dchip />
                            <span>{note}</span>
                            <span className="text-amber-300/90">· {t('feedSubscribersOnly')}</span>
                        </div>
                    )}
                </div>

                {!picks.length && (
                    <p className="text-center text-gray-400 text-sm py-6">
                        {loaded ? t('feedWaiting') : '…'}
                    </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {picks.map((stock, i) => (
                        <a
                            key={i}
                            href={`/stock/${encodeURIComponent(stock.code)}/`}
                            className="glass-card spotlight rounded-2xl p-6 block transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                                        <span>{stock.name}</span>
                                        <Dchip />
                                    </h3>
                                    <span className="text-sm text-gray-400">({stock.code})</span>
                                </div>
                                <div className={`px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1 ${stock.isPositive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                    {stock.isPositive ? '🚀' : '⚠️'} {stock.changePct}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                    <span className="text-gray-400">{t('livePicksCurrentPrice')}</span>
                                    <span className="text-white font-bold text-lg">{stock.price}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400">{t('livePicksSmartLiquidity')}</span>
                                    <span className="text-purple-400 font-bold">{stock.liquidity}% 🐋</span>
                                </div>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default LiveAiPicks;
